/**
 * Returns whether an allowlist entry can be used for exact pathname matching.
 * Unusable entries are skipped so a single bad pattern cannot break navigation.
 *
 * @param {unknown} entry - Candidate path pattern.
 * @returns {boolean} True when the entry is a non-empty absolute path without query or hash.
 */
const isUsablePathEntry = (entry: unknown): entry is string => {
  if (typeof entry !== 'string') {
    return false;
  }

  const trimmed = entry.trim();
  if (trimmed !== entry || trimmed.length === 0) {
    return false;
  }

  if (!trimmed.startsWith('/')) {
    return false;
  }

  if (trimmed.includes('?') || trimmed.includes('#')) {
    return false;
  }

  return true;
};

/**
 * Returns whether `pathname` is an exact ViewContent allowlist match.
 * An empty or absent allowlist returns false. Malformed entries are skipped.
 *
 * @param {string} pathname - Pathname from the App Router, including trailing slash.
 * @param {readonly string[]} [paths] - Allowlist for the resolved tenant; defaults to empty.
 * @returns {boolean} True only on an exact usable-entry match.
 */
export const matchesViewContentPath = (
  pathname: string,
  paths: readonly string[] = [],
): boolean => {
  try {
    if (!paths || paths.length === 0) {
      return false;
    }

    for (const entry of paths) {
      if (!isUsablePathEntry(entry)) {
        continue;
      }
      if (entry === pathname) {
        return true;
      }
    }

    return false;
  } catch {
    return false;
  }
};
